import { useState, useRef, useEffect } from 'react'
import { Bell, X, RotateCcw, Trash2, AlertTriangle, Flame, Clock } from 'lucide-react'
import { useNotificationStore } from '@/store'
import { usePromoAlerts } from '@/hooks'
import { formatCurrency, formatDate } from '@/lib/formatters'
import { PROMO_ALERT_COLORS } from '@/lib/constants'
import type { PromoAlert } from '@/types'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'

/**
 * Notification bell + dropdown history panel.
 *
 * Shows two sections:
 *   - Active: alerts the user has not dismissed (live, sorted by urgency)
 *   - Recently dismissed: alerts the user has cleared, with one-click undo
 *
 * The bell badge shows count of ACTIVE alerts. Click the bell to toggle the panel.
 */
export function NotificationBell() {
  const { active, dismissed } = usePromoAlerts()
  const dismiss = useNotificationStore((s) => s.dismiss)
  const undismiss = useNotificationStore((s) => s.undismiss)
  const clearAllDismissed = useNotificationStore((s) => s.clearAllDismissed)
  const [open, setOpen] = useState(false)
  const [confirmingClear, setConfirmingClear] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  // Close on outside click
  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  const activeCount = active.length
  const hasUrgent = active.some((a) => a.severity === 'expired' || a.severity === 'critical')

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="relative p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
        title="Notifications"
      >
        <Bell className="w-5 h-5" />
        {activeCount > 0 && (
          <span
            className={[
              'absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center text-[10px] font-bold rounded-full px-1',
              hasUrgent ? 'bg-red-500 text-white' : 'bg-amber-500 text-slate-900',
            ].join(' ')}
          >
            {activeCount > 9 ? '9+' : activeCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-96 bg-slate-900 border border-slate-700 rounded-xl shadow-2xl z-50 max-h-[80vh] overflow-hidden flex flex-col">
          <div className="flex items-center justify-between p-3 border-b border-slate-800">
            <div className="flex items-center gap-2">
              <Bell className="w-4 h-4 text-emerald-400" />
              <h3 className="font-semibold text-white text-sm">Notifications</h3>
            </div>
            <button onClick={() => setOpen(false)} className="p-1 text-slate-500 hover:text-white">
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="overflow-y-auto flex-1">
            {/* Active section */}
            <div className="p-3">
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
                Active ({active.length})
              </div>
              {active.length === 0 ? (
                <div className="text-sm text-slate-500 italic">No active alerts</div>
              ) : (
                <div className="space-y-2">
                  {active.map((alert) => (
                    <AlertCard
                      key={alert.id}
                      alert={alert}
                      action="dismiss"
                      onAction={() => dismiss(alert.id)}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Dismissed section */}
            {dismissed.length > 0 && (
              <div className="p-3 border-t border-slate-800">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Dismissed ({dismissed.length})
                  </div>
                  <button
                    onClick={() => setConfirmingClear(true)}
                    className="text-xs text-slate-500 hover:text-red-400 flex items-center gap-1"
                  >
                    <Trash2 className="w-3 h-3" />
                    Clear all
                  </button>
                </div>
                <div className="space-y-2">
                  {dismissed.map((alert) => (
                    <AlertCard
                      key={alert.id}
                      alert={alert}
                      action="restore"
                      onAction={() => undismiss(alert.id)}
                      muted
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <ConfirmDialog
        open={confirmingClear}
        title="Clear all dismissed alerts?"
        tone="danger"
        confirmLabel={`Clear ${dismissed.length}`}
        message={`This permanently removes the ${dismissed.length} alert${dismissed.length === 1 ? '' : 's'} from your dismissed history. New alerts for the same promo won't be affected.`}
        onConfirm={() => {
          clearAllDismissed()
          setConfirmingClear(false)
        }}
        onCancel={() => setConfirmingClear(false)}
      />
    </div>
  )
}

interface AlertCardProps {
  alert: PromoAlert
  action: 'dismiss' | 'restore'
  onAction: () => void
  muted?: boolean
}

function AlertCard({ alert, action, onAction, muted }: AlertCardProps) {
  const Icon =
    alert.severity === 'expired' ? Flame : alert.severity === 'critical' ? AlertTriangle : Clock

  return (
    <div
      className={[
        'flex items-start gap-2 p-2.5 rounded-lg border text-xs',
        muted ? 'bg-slate-950/40 border-slate-800 opacity-60' : PROMO_ALERT_COLORS[alert.severity],
      ].join(' ')}
    >
      <Icon className="w-4 h-4 shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <div className="font-semibold">{alert.debtName}</div>
        <div className="opacity-80 mt-0.5">
          {alert.currentAPR}% → <span className="font-bold">{alert.postPromoAPR}%</span> ·{' '}
          {alert.severity === 'expired' ? 'EXPIRED' : `${Math.ceil(alert.monthsRemaining)}mo left`}
        </div>
        <div className="opacity-70 mt-0.5">
          +{formatCurrency(alert.estimatedMonthlyInterestIncrease)}/mo ·{' '}
          {formatDate(alert.expiresAt)}
        </div>
      </div>
      <button
        onClick={onAction}
        title={action === 'dismiss' ? 'Dismiss' : 'Restore'}
        className="p-1 rounded hover:bg-white/10 opacity-60 hover:opacity-100 shrink-0"
      >
        {action === 'dismiss' ? (
          <X className="w-3.5 h-3.5" />
        ) : (
          <RotateCcw className="w-3.5 h-3.5" />
        )}
      </button>
    </div>
  )
}
